/**
 * Live public tournament page: the real-data twin of /demo.
 *
 * Branding comes from the organizing club's theme, ad zones are served with that
 * club's entitlements applied (so a Tour-plan club's audience sees no ads at all),
 * and every board on the event is switchable.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { LeaderboardPayload } from '@fairwaylive/shared';
import { AdProvider } from '../lib/adClient.tsx';
import { AdZone, InterstitialAd } from '../components/AdZone.tsx';
import { Leaderboard } from '../components/Leaderboard.tsx';
import { ErrorNote, HeroCarousel, LiveDot, Spinner } from '../components/Shell.tsx';
import { beacon } from '../lib/api.ts';
import { useFetch, useLive } from '../lib/useLive.ts';

interface TournamentDetail {
  tournament: {
    id: string;
    slug: string;
    name: string;
    subtitle: string | null;
    status: string;
    roundCount: number;
    currentRound: number;
    presentedBy: string | null;
    heroSceneKey: string | null;
    entryFeeCents: number | null;
    entryCount: number;
    isDemo: boolean;
  };
  organization: { id: string; name: string; slug: string } | null;
  theme: { primaryColor: string; accentColor: string; heroScenes: string[]; logoUrl: string | null; headline: string | null } | null;
  boards: Array<{ id: string; name: string; slug: string; scope: string; formatKey: string }>;
  rounds: Array<{ roundNumber: number; name: string | null; playedOn: string | null; status: string }>;
  realtime: { transport: string; pollSeconds: number };
}

export function TournamentPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error } = useFetch<TournamentDetail>(slug ? `/api/tournaments/${slug}` : null);

  if (loading) return <Spinner label="Loading event" />;
  if (error || !data) {
    return (
      <div className="mx-auto max-w-[820px] px-4 py-16">
        <ErrorNote
          message={error ?? 'Event not found'}
          hint="Live events need a database. The sample tournament at /demo runs without one."
        />
        <Link to="/demo" className="btn btn-primary mt-5">
          Open the live demo instead
        </Link>
      </div>
    );
  }

  return (
    <AdProvider
      surface="leaderboard"
      tournamentId={data.tournament.id}
      organizationId={data.organization?.id ?? null}
      isDemo={data.tournament.isDemo}
    >
      <TournamentBody detail={data} />
      <InterstitialAd />
    </AdProvider>
  );
}

function TournamentBody({ detail }: { detail: TournamentDetail }) {
  const [boardSlug, setBoardSlug] = useState(detail.boards[0]?.slug ?? 'gross');
  const live = useLive<LeaderboardPayload>({
    fetchPath: `/api/tournaments/${detail.tournament.id}/leaderboard/${boardSlug}`,
    streamPath: `/api/tournaments/${detail.tournament.id}/stream`,
    pollSeconds: detail.realtime.pollSeconds,
  });

  // Anonymous view accounting — powers the organizer's audience numbers.
  useEffect(() => {
    const start = Date.now();
    const report = () =>
      beacon('/api/analytics/views', {
        surface: 'leaderboard',
        tournamentId: detail.tournament.id,
        organizationId: detail.organization?.id,
        dwellSeconds: (Date.now() - start) / 1000,
        isDemo: detail.tournament.isDemo,
      });
    window.addEventListener('pagehide', report);
    return () => {
      window.removeEventListener('pagehide', report);
      report();
    };
  }, [detail]);

  const scenes = detail.theme?.heroScenes?.length ? detail.theme.heroScenes : [detail.tournament.heroSceneKey ?? 'sunset_finish'];

  return (
    <div className="pb-24">
      <HeroCarousel sceneKeys={scenes} height="h-[280px] sm:h-[360px]">
        <div className="mx-auto w-full max-w-[1240px] px-4 pb-8">
          {detail.tournament.presentedBy ? (
            <p className="eyebrow text-[var(--color-sand)]">Presented by {detail.tournament.presentedBy}</p>
          ) : null}
          <h1 className="board-title mt-1 text-[32px] font-bold leading-none sm:text-[48px]">{detail.tournament.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[var(--color-sand)]">
            {detail.organization ? <span>{detail.organization.name}</span> : null}
            <span>
              Round {detail.tournament.currentRound} of {detail.tournament.roundCount}
            </span>
            <span>{detail.tournament.entryCount} entries</span>
            <span className="uppercase tracking-wider">{detail.tournament.status.replace(/_/g, ' ')}</span>
            <LiveDot transport={live.transport} />
          </div>
        </div>
      </HeroCarousel>

      <div className="mx-auto max-w-[1240px] px-4">
        <div className="pt-4">
          <AdZone zoneKey="top_banner" />
        </div>

        <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                {detail.boards.map((board) => (
                  <button
                    key={board.slug}
                    type="button"
                    onClick={() => setBoardSlug(board.slug)}
                    className={`btn !py-1.5 !text-[13px] ${boardSlug === board.slug ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    {board.name}
                  </button>
                ))}
              </div>
              <Link to={`/t/${detail.tournament.slug}/bigscreen`} className="btn btn-ghost !py-1.5 !text-[13px]">
                ⛶ Clubhouse display
              </Link>
            </div>

            {live.error ? <ErrorNote message={live.error} /> : null}
            {live.data ? <Leaderboard payload={live.data} adEveryNRows={10} /> : <Spinner label="Loading board" />}
          </div>

          <aside className="space-y-4">
            <AdZone zoneKey="sidebar_primary" />
            <AdZone zoneKey="sidebar_secondary" />
            {detail.tournament.entryFeeCents ? (
              <div className="card p-4">
                <h3 className="eyebrow mb-2">Entries</h3>
                <p className="text-sm text-[var(--color-muted)]">
                  Entry is open through the platform. Fees are collected online and paid over to the club.
                </p>
              </div>
            ) : null}
            <AdZone zoneKey="affiliate_slot" />
          </aside>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[color-mix(in_oklab,var(--color-fairway-400)_22%,transparent)] bg-[color-mix(in_oklab,var(--color-fairway-900)_94%,transparent)] px-3 py-2 backdrop-blur">
        <div className="mx-auto max-w-[760px]">
          <AdZone zoneKey="footer_banner" />
        </div>
      </div>
    </div>
  );
}
